import { createContext, useContext, useEffect, useState } from "react";

/** The space between the Run's column and a margin; a margin's narrowest and widest. */
const GAP_REM = 1;
const MARGIN_MIN_REM = 15;
const MARGIN_MAX_REM = 22;

export const RUN_MARGIN_GAP = `${String(GAP_REM)}rem`;
/**
 * A margin beside the Run's column, positioned against it: as wide as the pane leaves beside
 * the centred column (`100%` is the column, `100cqw` the pane), up to the width a side pane
 * needs; none in a pane no wider than the column.
 */
export const RUN_MARGIN_WIDTH = `min(${String(MARGIN_MAX_REM)}rem, calc((100cqw - 100%) / 2 - ${RUN_MARGIN_GAP}))`;

/**
 * The Run page's margins beside its column, where the step in view puts its side panes: a
 * change set's review its outline left and its context right (Johannes, 4 Nov). `null`
 * while they are too narrow for a pane; the step then keeps its panes in the column.
 */
export interface RunMargins {
  readonly left: HTMLElement;
  readonly right: HTMLElement;
}

export const RunMarginsContext = createContext<RunMargins | null>(null);

/** The margins of the Run page this view is in, or `null`. */
export function useRunMargins(): RunMargins | null {
  return useContext(RunMarginsContext);
}

/** Whether a margin is wide enough for a side pane, as the pane's width changes. */
export function useMarginRoom(margin: HTMLElement | null): boolean {
  const [room, setRoom] = useState(false);
  useEffect(() => {
    if (margin === null || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setRoom(margin.clientWidth >= MARGIN_MIN_REM * rem);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(margin);
    measure();
    return () => {
      observer.disconnect();
    };
  }, [margin]);
  return room;
}
